/**
 * Idempotent document-head manager for the marketing SPA.
 *
 * React Router swaps pages client-side, so per-page <title>, description,
 * canonical, Open Graph and Twitter tags + JSON-LD breadcrumbs have to be
 * upserted at runtime. Everything here is driven by PAGE_REGISTRY + SITE_ORIGIN
 * (website/src/manifests/site.ts) and the codegen-emitted SITE_JSONLD — no
 * per-page hardcoding (Rule 18/22).
 *
 * Every tag we manage carries `data-ix-head` so re-runs update in place rather
 * than appending duplicates, and so a future prerender pass can tell ours apart
 * from index.html's static defaults.
 */
import type { SitePage } from '../manifests/site';
import { SITE_ORIGIN } from '../manifests/site';
import { SITE_JSONLD } from '../generated/structured-data';
import { PAGE_JSONLD } from '../generated/page-jsonld';

const MANAGED = 'data-ix-head';

function absolute(pathOrUrl: string): string {
  if (/^https?:\/\//.test(pathOrUrl)) return pathOrUrl;
  return `${SITE_ORIGIN}${pathOrUrl.startsWith('/') ? '' : '/'}${pathOrUrl}`;
}

/** Route → OG card slug, matching the og-cards generator (which emits the PNGs). */
function ogSlug(route: string): string {
  const s = route.replace(/^\/+|\/+$/g, '').replace(/\//g, '-');
  return s || 'home';
}

/** A managed <meta>/<link> directive — matched by attr=value, sets attrs. */
export interface ManagedTag {
  readonly tag: 'meta' | 'link';
  readonly match: { readonly attr: string; readonly value: string };
  readonly set: Record<string, string>;
}

/** The complete head model for a page: title, managed tags, JSON-LD graphs.
 *  Pure (no DOM) so both the runtime DOM path (applyHead) and the build-time
 *  prerender serializer derive identical output from one source of truth. */
export interface HeadModel {
  readonly title: string;
  readonly tags: readonly ManagedTag[];
  readonly jsonLd: readonly object[];
}

/** Compute every head directive for a page from PAGE_REGISTRY + SITE_ORIGIN. */
export function buildHead(page: SitePage): HeadModel {
  // Trailing slash IS the canonical form: Cloudflare Pages 308s /pricing → /pricing/,
  // so a slashless canonical/sitemap URL put a redirect hop in front of every page
  // (SEO audit H1, 2026-08-15). canonical = served URL = sitemap URL.
  const canonical = absolute(page.route === '/' ? '/' : `${page.route}/`);
  const ogImage = absolute(page.ogImage ?? `/og/${ogSlug(page.route)}.png`);

  const tags: ManagedTag[] = [
    { tag: 'meta', match: { attr: 'name', value: 'description' }, set: { content: page.description } },
    { tag: 'link', match: { attr: 'rel', value: 'canonical' }, set: { href: canonical } },
    { tag: 'meta', match: { attr: 'property', value: 'og:type' }, set: { content: 'website' } },
    { tag: 'meta', match: { attr: 'property', value: 'og:url' }, set: { content: canonical } },
    { tag: 'meta', match: { attr: 'property', value: 'og:title' }, set: { content: page.title } },
    { tag: 'meta', match: { attr: 'property', value: 'og:description' }, set: { content: page.description } },
    { tag: 'meta', match: { attr: 'property', value: 'og:image' }, set: { content: ogImage } },
    { tag: 'meta', match: { attr: 'name', value: 'twitter:card' }, set: { content: 'summary_large_image' } },
    { tag: 'meta', match: { attr: 'name', value: 'twitter:title' }, set: { content: page.title } },
    { tag: 'meta', match: { attr: 'name', value: 'twitter:description' }, set: { content: page.description } },
    { tag: 'meta', match: { attr: 'name', value: 'twitter:image' }, set: { content: ogImage } },
  ];

  // Site-wide graphs + per-route breadcrumb + any per-page graphs (Article on
  // insight essays / the manifesto; FAQPage where a page declares a faq block).
  const perPage = PAGE_JSONLD[page.route] ?? [];
  return { title: page.title, tags, jsonLd: [...SITE_JSONLD, breadcrumb(page), ...perPage] };
}

/** Find-or-create a managed <meta>/<link> matched by attr=value. */
function upsertTag(
  tag: 'meta' | 'link',
  match: { attr: string; value: string },
  set: Record<string, string>,
): void {
  const selector = `${tag}[${match.attr}="${match.value}"]`;
  let el = document.head.querySelector<HTMLElement>(selector);
  if (!el) {
    el = document.createElement(tag);
    el.setAttribute(match.attr, match.value);
    el.setAttribute(MANAGED, '');
    document.head.appendChild(el);
  }
  for (const [k, v] of Object.entries(set)) el.setAttribute(k, v);
}

/** Replace all managed JSON-LD <script> tags with the given graphs. */
function setJsonLd(graphs: readonly object[]): void {
  document.head
    .querySelectorAll(`script[type="application/ld+json"][${MANAGED}]`)
    .forEach((n) => n.remove());
  for (const graph of graphs) {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute(MANAGED, '');
    script.textContent = JSON.stringify(graph);
    document.head.appendChild(script);
  }
}

/** A schema.org BreadcrumbList from the route's path segments. */
function breadcrumb(page: SitePage): object {
  const segments = page.route.split('/').filter(Boolean);
  const crumbs: { name: string; path: string }[] = [{ name: 'Home', path: '' }];
  let acc = '';
  for (const seg of segments) {
    acc += `/${seg}`;
    const label = seg
      .split('-')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
    crumbs.push({ name: label, path: acc });
  }
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: crumbs.map((c, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: c.name,
      item: absolute(c.path ? `${c.path}/` : '/'),
    })),
  };
}

/** Apply all head tags for a page. Safe to call on every navigation. Reuses the
 *  same buildHead() model the prerender serializes, so runtime and static HTML
 *  never drift. */
export function applyHead(page: SitePage): void {
  const { title, tags, jsonLd } = buildHead(page);
  document.title = title;
  for (const t of tags) upsertTag(t.tag, t.match, t.set);
  setJsonLd([...jsonLd]);
}
